// Tiny markdown renderer for note bodies. Ported from the design's
// helpers.jsx renderMarkdown/renderInline. Supports: paragraphs (blank
// lines), **bold**, *italic*, > blockquote, ## H2, - list items.
//
// Not a full CommonMark parser — for v0.1 the classifier produces simple
// notes. Swap to a real library if we ever need tables, images, etc.

import { Fragment, type ReactNode } from "react";

function renderInline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let rest = text;
  let key = 0;
  while (rest.length > 0) {
    const boldMatch = rest.match(/\*\*(.+?)\*\*/);
    const italicMatch = rest.match(/\*(.+?)\*/);
    const first = [boldMatch, italicMatch]
      .filter((m): m is RegExpMatchArray => m !== null)
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))[0];
    if (!first) {
      parts.push(<Fragment key={key++}>{rest}</Fragment>);
      break;
    }
    const idx = first.index ?? 0;
    if (idx > 0) parts.push(<Fragment key={key++}>{rest.slice(0, idx)}</Fragment>);
    if (first === boldMatch) parts.push(<strong key={key++}>{first[1]}</strong>);
    else parts.push(<em key={key++}>{first[1]}</em>);
    rest = rest.slice(idx + first[0].length);
  }
  return parts;
}

export function renderMarkdown(src: string): ReactNode[] {
  const blocks = src.split(/\n\n+/);
  return blocks.map((rawBlock, i) => {
    const block = rawBlock.replace(/\n+$/, "");
    if (block.startsWith("## ")) {
      return <h2 key={i}>{renderInline(block.slice(3))}</h2>;
    }
    if (block.startsWith("> ")) {
      const inner = block
        .split("\n")
        .map((l) => l.replace(/^>\s?/, ""))
        .join(" ");
      return <blockquote key={i}>{renderInline(inner)}</blockquote>;
    }
    if (/^-\s/.test(block)) {
      const items = block
        .split("\n")
        .filter((l) => l.startsWith("- "))
        .map((l, j) => <li key={j}>{renderInline(l.slice(2))}</li>);
      return <ul key={i}>{items}</ul>;
    }
    return <p key={i}>{renderInline(block.replace(/\n/g, " "))}</p>;
  });
}
